
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  StyledGridBadges
} from "./CardGoals.styles";
import { ACHIEVEMENTS_GRID_MAX_ITEMS, CardGoalsProps } from "./CardGoals.types";
import {
  BadgeGoals,
  Link, Modal
} from "../../components";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { NAVBAR_ITEMS } from "../../consts";
import { useDownloadCertificate } from "../../queries";
import { useNavbarStore } from "../../stores";
import { getError } from "../../utils/error";
import { downloadBlobFile } from "../../utils/general";
import { CertificateInfo } from "../CertificateButton/CertificateButtonContainer.types";
import { StyledAnchor } from "../PreviewWidget/Carousel.styles";

export function CardGoalsContainer({ achievements }: CardGoalsProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const setNavbarItem = useNavbarStore(state => state.setNavbarItem);
  const [certificatesInDownload, setCertificatesInDownload] = useState<number[] | null>(null);
  const [showModal, setShowModal] = useState(false);

  const {
    error: downloadCertificateError,
    isError: isDownloadCertificateError,
    mutateAsync: mutateDownloadCertificate } = useDownloadCertificate();

  useEffect(() => {
    isDownloadCertificateError && setShowModal(true);
  }, [isDownloadCertificateError]);

  const downloadCertificateHandler = async (certificateInfo: CertificateInfo)=> {
    setCertificatesInDownload(certificatesInDownload => {
      return certificatesInDownload ? [...certificatesInDownload, certificateInfo.certificateId] :
        [certificateInfo.certificateId];
    });

    try {
      const certificateResponseBlob = await mutateDownloadCertificate(certificateInfo);
      downloadBlobFile({
        blobFile: certificateResponseBlob,
        fileName: `issued_certificate_lo_${certificateInfo.learningObjectId}_learner_${certificateInfo.learnerId}.pdf`
      });
    } finally {
      setCertificatesInDownload(certificatesInDownload => {
        if (certificatesInDownload && certificatesInDownload.length > 0) {
          const filteredIds = certificatesInDownload.filter(id => id !== certificateInfo.certificateId);
          if (filteredIds.length < 1) {
            return null;
          }
        }
        return null;
      });
    }
  };

  return (
    <Box 
      sx={ {
        padding: "0rem 1rem"
      } }
    >
      <StyledGridBadges container>
        { achievements?.slice(0,6).map((item, index)=> (
          <BadgeGoals
            key={ index.toString() }
            small={ true }
            badge={ item.badge }
            color={ theme.customColors.textWidget }
            certificate={ item.certificate }
            downloadCertificate={ downloadCertificateHandler }
            isDownloading={ !!( item.certificate?.id &&
              certificatesInDownload &&
              certificatesInDownload.indexOf(item.certificate?.id)>=0)
            }
          />
        )) }
      </StyledGridBadges>

      <Modal
        open={ showModal && isDownloadCertificateError }
        onClose={ ()=> { setShowModal(false) } }
        description={ getError(downloadCertificateError)?.[0]?.message ?? "" }
        title={ getError(downloadCertificateError)?.[0]?.name ?? "" }
      />

      { (achievements && achievements.length > ACHIEVEMENTS_GRID_MAX_ITEMS) && (
        <StyledAnchor>
          <Box
            sx={ {
              display: "flex",
              justifyContent: "center" // center children horizontally
            } }
          >
            <Link href="/profile"
              tabIndex={ 0 }
              aria-label={ t("view_all_certicates") }
              onClick={ ()=> { setNavbarItem(NAVBAR_ITEMS.NONE) } }
              sx={ {
                width: "fit-content",
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.text?.primary}`,
                  outlineOffset: "2px"
                }
              } }
            >
              <ButtonDigited
                tabIndex={ -1 }
                typology="tertiary"
                buttonSize="sm"
                text={ t("view_all") + " " } 
              />
            </Link>
          </Box>
        </StyledAnchor>
      ) }
    </Box>
  );
}
